import { useEffect, useRef, type ReactNode } from 'react'
import { Button, cx, ErrorState, Loading } from './primitives'

/**
 * A modal on the native <dialog> element.
 *
 * showModal() brings the focus trap, the Escape key, the inert background and the
 * top layer with it - all the parts a hand-rolled modal gets wrong. No dependency
 * buys less than the browser already gives.
 */
export function Dialog({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  wide,
  document: documentSize,
  loading,
  error,
  onRetry,
}: {
  open: boolean
  title: string
  description?: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  /** For content that reads as columns - a history's field, before and after. */
  wide?: boolean
  /**
   * For a document being read rather than a form being filled in.
   *
   * <p>
   * A quotation is checked page by page before it is sent, and a 560px box showing half an
   * A4 page is a preview nobody can actually review in - which means the check happens
   * after the download, or not at all. This takes as much of the viewport as it can while
   * still reading as a dialog over the page it came from.
   * </p>
   */
  document?: boolean
  /**
   * The dialog's own data is still on its way.
   *
   * <p>
   * A form drawn before its options arrive is a form with the wrong answers in it: empty
   * boxes that fill in a moment later, a checkbox list that grows under the pointer, and a
   * Save that would write what had loaded so far. So the body waits and the footer goes
   * inert - Escape, the backdrop and the close button are outside it, so there is always a
   * way out.
   * </p>
   */
  loading?: boolean
  /**
   * Structurally an <c>ApiError</c>, without the kit depending on it. A dialog that failed
   * to load must say so: the alternative is an empty form whose Save wipes what it could
   * not read.
   */
  error?: { title: string; detail?: string | undefined } | undefined
  onRetry?: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      // Escape closes the dialog itself; this keeps React's state in step with it.
      onClose={onClose}
      onClick={(event) => {
        // A click on the backdrop lands on the dialog element itself.
        if (event.target === ref.current) onClose()
      }}
      className={
        cx(
          // m-auto is load-bearing. The user agent centres a modal <dialog> with
          // margin: auto, and Tailwind's preflight resets margin to 0 on every
          // element - which is what pinned every dialog to the top-left corner.
          'relative m-auto rounded-lg border border-line bg-surface p-0 text-ink shadow-float backdrop:bg-black/40',
          documentSize
            ? 'w-[min(96vw,1180px)]'
            : wide
              ? 'w-[min(94vw,860px)]'
              : 'w-[min(94vw,560px)]',
        )
      }
    >
      <div className="border-b border-line-soft py-4 pl-5 pr-14">
        <h2 className="text-base font-semibold">{title}</h2>
        {description && <p className="mt-0.5 text-[13px] text-muted">{description}</p>}
      </div>

      <div className={cx('overflow-y-auto px-5 py-4', documentSize ? 'max-h-[82vh]' : 'max-h-[60vh]')}>
        {error ? (
          <ErrorState title={error.title} detail={error.detail} onRetry={onRetry} />
        ) : loading ? (
          <Loading />
        ) : (
          children
        )}
      </div>

      {/* inert rather than hidden: the footer keeps its height, so the dialog does not
          change size the instant the data lands and move Save out from under the pointer. */}
      <div
        inert={loading || error !== undefined || undefined}
        className={cx(
          'flex items-center justify-end gap-2 border-t border-line-soft px-5 py-3',
          (loading || error) && 'opacity-40',
        )}
      >
        {footer ?? (
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      {/* Always present, whatever the footer holds. Escape and a backdrop click already
          closed a dialog and neither is visible, so a dialog whose footer is a single
          "Disband team" reads as a screen with one way out - and that way out is
          destructive. That is how the Sales Team dialog ended up with no way to leave it.

          Rendered last and positioned, rather than written into the header. showModal()
          focuses the first focusable descendant, and a close button first in the DOM
          would take the caret away from the first field of every form dialog in the
          application. Last in the DOM, top-right on the screen. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-3.5 top-3.5 grid size-7 place-items-center rounded-md text-muted transition-colors hover:bg-sunken hover:text-ink focus-visible:bg-sunken focus-visible:text-ink"
      >
        <svg aria-hidden viewBox="0 0 12 12" className="size-3">
          <path d="m3 3 6 6M9 3l-6 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </dialog>
  )
}
